import React, { useState, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Skeleton from "@mui/material/Skeleton";
import { Business } from "@mui/icons-material";

interface CompanyLogoProps {
  domain: string;
  size?: number;
  variant?: "circular" | "rounded" | "square";
  sx?: object;
  fallback?: React.ReactNode;
}

export const CompanyLogo: React.FC<CompanyLogoProps> = React.memo(
  ({ domain, size = 60, variant = "rounded", sx = {}, fallback }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const logoUrl = domain
      ? `https://img.logo.dev/${domain}?token=pk_AtrL6YDSTkCOYhjq7FMKfg&size=${size}`
      : "";

    useEffect(() => {
      if (!domain) {
        setLoading(false);
        setError(true);
        return;
      }

      setLoading(true);
      setError(false);

      const img = new Image();
      img.src = logoUrl;

      img.onload = () => {
        setLoading(false);
        setError(false);
      };

      img.onerror = () => {
        setLoading(false);
        setError(true);
      };

      // Cleanup function to prevent state updates on unmounted component
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [domain, logoUrl]); // Depend on domain and logoUrl

    // Map Avatar variants to Skeleton variants
    const getSkeletonVariant = () => {
      if (variant === "circular") return "circular";
      return "rectangular"; // Both "rounded" and "square" use rectangular for Skeleton
    };

    if (loading) {
      return (
        <Skeleton
          variant={getSkeletonVariant()}
          width={size}
          height={size}
          sx={{
            ...(variant === "rounded" && { borderRadius: 1 }),
            ...sx,
          }}
        />
      );
    }

    if (error) {
      return (
        <Avatar
          variant={variant}
          sx={{
            width: size,
            height: size,
            backgroundColor: "grey.200",
            color: "grey.500",
            ...sx,
          }}
        >
          {fallback || <Business />}
        </Avatar>
      );
    }

    return (
      <Avatar
        src={logoUrl}
        alt={domain}
        variant={variant}
        sx={{
          width: size,
          height: size,
          "& img": {
            objectFit: "contain",
          },
          ...sx,
        }}
      />
    );
  }
);

CompanyLogo.displayName = "CompanyLogo";
